import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'

import {
  Divider,
  Flex,
  Form,
  IconNotification,
  InfoAlert,
  PrimaryButton,
  Space,
  TertiaryButton,
  WithFormControlSelect,
} from '@procurify/ui'
import {
  type FormikContextType,
  type FormikHelpers,
  FormikProvider,
  useFormik,
} from 'formik'
import { type SelectInstance } from 'react-select'

import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExternalBankAccountTypes } from '@webapp/platform/banking/entities/enums/ExternalBankAccountTypeEnum'
import {
  buildExternalBankAccountFormInitialValues,
  buildExternalBankAccountFormValidationSchema,
  type ExternalBankAccountFormField,
  type ExternalBankAccountFormSchema,
  type ExternalBankAccountFormStatus,
  type ExternalBankAccountTypeOption,
  getStandingServerErrors,
} from './ExternalBankAccountFormSchema'
import messages from './messages'

export interface IExternalBankAccountFormProps {
  /** bill_pay && !FT_4280_BILL_PAYMENT_CONSOLIDATION — FS-derived (BHV-013) */
  restrictTypeToOther: boolean
  /** Initial state of the "Create another after save" box */
  initialCreateAnother?: boolean
  /** Mutation in-flight state from the presenter */
  isSubmitting: boolean
  /** Resolves `true` once the account is saved, `false` when the save failed */
  onSubmit: (
    values: ExternalBankAccountFormSchema,
    helpers: FormikHelpers<ExternalBankAccountFormSchema>
  ) => Promise<boolean>
  onCancel: () => void
}

/**
 * "Add account" bank form content (BHV-012/BHV-032) — form content only; the
 * Feature Set owns the ModalDialog shell.
 */
export const ExternalBankAccountForm = ({
  restrictTypeToOther,
  initialCreateAnother = false,
  isSubmitting,
  onSubmit,
  onCancel,
}: IExternalBankAccountFormProps) => {
  const intl = useProcurifyIntl()

  // Only Other / Check / ACH are selectable — the Angular EFT branch is dead
  // code and dropped (BHV-012).
  const typeOptions: ExternalBankAccountTypeOption[] = useMemo(
    () => [
      {
        value: ExternalBankAccountTypes.OTHER,
        label: intl.formatMessage(messages.typeOther),
      },
      {
        value: ExternalBankAccountTypes.CHECK,
        label: intl.formatMessage(messages.typeCheck),
      },
      {
        value: ExternalBankAccountTypes.ACH,
        label: intl.formatMessage(messages.typeAch),
      },
    ],
    [intl]
  )

  // Fields validate from the first submit on. An error mounting under the
  // autofocused name field on blur would push the submit button down between
  // mousedown and click, so the first click would be lost.
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const shouldFocusAfterFailedSaveRef = useRef(false)

  const formik: FormikContextType<ExternalBankAccountFormSchema> =
    useFormik<ExternalBankAccountFormSchema>({
      initialValues: {
        ...buildExternalBankAccountFormInitialValues(
          typeOptions,
          restrictTypeToOther
        ),
        createAnother: initialCreateAnother,
      },
      validationSchema: buildExternalBankAccountFormValidationSchema(intl),
      // Each validation replaces `errors`, so the server errors of the last
      // failed save are merged back in: one stays on its field until the user
      // changes that field, and blocks a resubmit of the rejected value.
      validate: (values) =>
        getStandingServerErrors(
          values,
          formik.status as ExternalBankAccountFormStatus | undefined,
          restrictTypeToOther
        ),
      validateOnChange: hasSubmitted,
      validateOnBlur: hasSubmitted,
      onSubmit: async (values, helpers) => {
        const isSaved = await onSubmit(values, helpers)

        shouldFocusAfterFailedSaveRef.current = !isSaved
      },
    })

  useEffect(() => {
    if (formik.submitCount > 0) {
      setHasSubmitted(true)
    }
  }, [formik.submitCount])

  const nameInputRef = useRef<HTMLInputElement>(null)
  const typeSelectRef =
    useRef<SelectInstance<ExternalBankAccountTypeOption, false>>(null)
  const currencySelectRef = useRef<SelectInstance<Currency, false>>(null)
  const descriptionInputRef = useRef<HTMLInputElement>(null)
  const startNumberInputRef = useRef<HTMLInputElement>(null)
  const bankNameInputRef = useRef<HTMLInputElement>(null)
  const bankRoutingNumberInputRef = useRef<HTMLInputElement>(null)
  const companyNameInputRef = useRef<HTMLInputElement>(null)
  const companyRoutingNumberInputRef = useRef<HTMLInputElement>(null)
  const companyTaxIdInputRef = useRef<HTMLInputElement>(null)
  const glCodeInputRef = useRef<HTMLInputElement>(null)

  // The fields disable while a save runs, so the browser drops focus from the
  // control that submitted. Once a failed save re-enables them, focus goes to
  // the first field showing an error, or to the name when the error only
  // shows in a toast.
  useEffect(() => {
    if (!shouldFocusAfterFailedSaveRef.current || formik.isSubmitting) return

    shouldFocusAfterFailedSaveRef.current = false

    // In the order the fields render; a type's own fields mount only for it
    const fieldFocusTargets: [
      ExternalBankAccountFormField,
      RefObject<{ focus: () => void }>,
    ][] = [
      ['name', nameInputRef],
      ['type', typeSelectRef],
      ['currency', currencySelectRef],
      ['description', descriptionInputRef],
      ['startNumber', startNumberInputRef],
      ['bankName', bankNameInputRef],
      ['bankRoutingNumber', bankRoutingNumberInputRef],
      ['companyName', companyNameInputRef],
      ['companyRoutingNumber', companyRoutingNumberInputRef],
      ['companyTaxId', companyTaxIdInputRef],
      ['glCode', glCodeInputRef],
    ]

    const [, focusTargetRef] =
      fieldFocusTargets.find(
        ([field, fieldRef]) => formik.errors[field] && fieldRef.current
      ) ?? fieldFocusTargets[0]

    focusTargetRef.current?.focus()
  }, [formik.isSubmitting, formik.errors])

  const selectedType = formik.values.type?.value
  const isAch = selectedType === ExternalBankAccountTypes.ACH

  const glCodeField = (
    <Form.TextField
      id='bank-account-gl-code'
      name='glCode'
      label={intl.formatMessage(messages.glCodeLabel)}
      placeholder={intl.formatMessage(messages.glCodePlaceholder)}
      inputRef={glCodeInputRef}
    />
  )

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <InfoAlert icon={<IconNotification />}>
            {intl.formatMessage(messages.securityNotice)}
          </InfoAlert>

          <Flex.Row>
            <Flex.Item width={1 / 2}>
              <Form.TextField
                id='bank-account-name'
                name='name'
                label={intl.formatMessage(messages.nameLabel)}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                required
                autoFocus
                inputRef={nameInputRef}
              />
            </Flex.Item>
            {!restrictTypeToOther && (
              <Flex.Item width={1 / 4}>
                <Form.Select
                  id='bank-account-type'
                  name='type'
                  label={intl.formatMessage(messages.typeLabel)}
                  placeholder={intl.formatMessage(messages.typePlaceholder)}
                  options={typeOptions}
                  required
                  selectRef={typeSelectRef}
                />
              </Flex.Item>
            )}
            <Flex.Item width={restrictTypeToOther ? 1 / 2 : 1 / 4}>
              <WithFormControlSelect
                id='bank-account-currency'
                name='currency'
                label={intl.formatMessage(messages.currencyLabel)}
                required
              >
                <CurrencySelect selectRef={currencySelectRef} />
              </WithFormControlSelect>
            </Flex.Item>
          </Flex.Row>

          {selectedType === ExternalBankAccountTypes.OTHER && (
            <Flex.Row>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-description'
                  name='description'
                  label={intl.formatMessage(messages.descriptionLabel)}
                  placeholder={intl.formatMessage(
                    messages.descriptionPlaceholder
                  )}
                  required
                  inputRef={descriptionInputRef}
                />
              </Flex.Item>
              <Flex.Item width={1 / 2}>{glCodeField}</Flex.Item>
            </Flex.Row>
          )}

          {selectedType === ExternalBankAccountTypes.CHECK && (
            <Flex.Row>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-start-number'
                  name='startNumber'
                  label={intl.formatMessage(messages.startNumberLabel)}
                  placeholder={intl.formatMessage(
                    messages.startNumberPlaceholder
                  )}
                  required
                  inputRef={startNumberInputRef}
                />
              </Flex.Item>
              <Flex.Item width={1 / 2}>{glCodeField}</Flex.Item>
            </Flex.Row>
          )}

          {isAch && (
            <Flex.Row>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-bank-name'
                  name='bankName'
                  label={intl.formatMessage(messages.bankNameLabel)}
                  placeholder={intl.formatMessage(messages.bankNamePlaceholder)}
                  required
                  inputRef={bankNameInputRef}
                />
              </Flex.Item>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-bank-routing-number'
                  name='bankRoutingNumber'
                  label={intl.formatMessage(messages.bankRoutingNumberLabel)}
                  placeholder={intl.formatMessage(
                    messages.bankRoutingNumberPlaceholder
                  )}
                  required
                  inputRef={bankRoutingNumberInputRef}
                />
              </Flex.Item>
            </Flex.Row>
          )}

          {isAch && (
            <Flex.Row>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-company-name'
                  name='companyName'
                  label={intl.formatMessage(messages.companyNameLabel)}
                  placeholder={intl.formatMessage(
                    messages.companyNamePlaceholder
                  )}
                  required
                  inputRef={companyNameInputRef}
                />
              </Flex.Item>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-company-routing-number'
                  name='companyRoutingNumber'
                  label={intl.formatMessage(messages.companyRoutingNumberLabel)}
                  placeholder={intl.formatMessage(
                    messages.companyRoutingNumberPlaceholder
                  )}
                  required
                  inputRef={companyRoutingNumberInputRef}
                />
              </Flex.Item>
            </Flex.Row>
          )}

          {isAch && (
            <Flex.Row>
              <Flex.Item width={1 / 2}>
                <Form.TextField
                  id='bank-account-company-tax-id'
                  name='companyTaxId'
                  label={intl.formatMessage(messages.companyTaxIdLabel)}
                  placeholder={intl.formatMessage(
                    messages.companyTaxIdPlaceholder
                  )}
                  required
                  inputRef={companyTaxIdInputRef}
                />
              </Flex.Item>
              <Flex.Item width={1 / 2}>{glCodeField}</Flex.Item>
            </Flex.Row>
          )}

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <Space.Item>
              <Form.Checkbox
                id='bank-account-create-another'
                name='createAnother'
                disabled={isSubmitting}
              >
                {intl.formatMessage(messages.createAnotherCheckbox)}
              </Form.Checkbox>
            </Space.Item>
            <Space.Item>
              <TertiaryButton
                type='button'
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {intl.formatMessage(messages.cancelButton)}
              </TertiaryButton>
              <PrimaryButton type='submit' isLoading={isSubmitting}>
                {intl.formatMessage(messages.addAccountButton)}
              </PrimaryButton>
            </Space.Item>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

ExternalBankAccountForm.displayName = 'ExternalBankAccountForm'
